import { ReactNode, createContext, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { Client, Qos } from 'paho-mqtt'
import { MQTT_API } from '../utils/api'
import { useCurrentUser } from '../hooks/contexts/currentUserContext'
import { Instrument, TouchMode, SONG_TYPES } from '../utils/types'
import { useConnectedInstruments, useUpdateConnectedInstrument } from '../hooks/contexts/connectedInstrumentsContext'
import { cleanupOfflineTimers, generateRandomClientId, markOffline, markPresent, updateFromHeartbeat, updateFromNotification } from './mqttHelpers'
import { useChangeOTAUpdateData } from '../hooks/contexts/otaUpdateContext'
import { HeartbeatMessage, NotificationMessage, RequestName, TopicKind } from './mqttTopics'

interface MqttHandlerContextProps {
    mqttIsConnected: boolean
    postChangeSong: ({ message, deviceId, songType }: { message: string; deviceId: string; songType?: SONG_TYPES }) => void
    postChangeVolume: (message: string, instrumentId: string) => void
    postChangeMasterVolume: (message: string) => void
    postResetNetwork: (instrumentId: string) => void
    postSetInstrumentMode: ({ deviceId, touchMode }: { deviceId?: string; touchMode: TouchMode }) => void
    postSendOTAVersionRequest: (deviceId: string) => void
    postSendOTAUpdateRequest: (deviceId: string, url: string) => void
    postSendOTARebootRequest: (deviceId: string) => void
    changeArrangement: ({ arrangement, deviceId }: { arrangement: string; deviceId?: string }) => void
    changeInstrumentMode: ({ deviceId, touchMode }: { deviceId?: string; touchMode: TouchMode }) => void
    changeVolume: ({ volume, deviceId }: { volume: number; deviceId: string }) => void
    changeMasterVolume: ({ volume }: { volume: number }) => void
}

const MqttHandlerContext = createContext<MqttHandlerContextProps>({
    mqttIsConnected: false,
    postChangeSong: () => {},
    postChangeVolume: () => {},
    postChangeMasterVolume: () => {},
    postResetNetwork: () => {},
    postSetInstrumentMode: () => {},
    postSendOTAVersionRequest: () => {},
    postSendOTAUpdateRequest: () => {},
    postSendOTARebootRequest: () => {},
    changeArrangement: () => {},
    changeInstrumentMode: () => {},
    changeVolume: () => {},
    changeMasterVolume: () => {}
})

const options = {
    host: 'mqtt-proxy.stordatornfunki.workers.dev',
    port: 443,
    protocol: 'wss',
    userName: MQTT_API.CREDITS.USERNAME,
    password: MQTT_API.CREDITS.PASSWORD,

    //////////////////////////////////////////////////////
    // IMPORTANT FOR iOS PWA
    //////////////////////////////////////////////////////

    keepAliveInterval: 30,

    cleanSession: true
}

const OFFLINE_AFTER_MS = 15_000
const CHECK_EVERY_MS = 1_000
const LEGACY_PROTOCOL = '1.0'
const DEFAULT_NEW_PROTOCOL = '2.0'

function safeJsonParse<T>(value: string): T | undefined {
    try {
        return JSON.parse(value) as T
    } catch {
        return undefined
    }
}

function normalizePresence(payload: string): 'ONLINE' | 'OFFLINE' | undefined {
    const normalized = payload.trim().toUpperCase()

    if (normalized === 'ONLINE') return 'ONLINE'
    if (normalized === 'OFFLINE') return 'OFFLINE'

    return undefined
}

function parseTopic(topic: string): { deviceId?: string; topicKind?: string } {
    const parts = topic.split('/').filter(Boolean)

    if (parts.length !== 2) return {}

    return {
        deviceId: parts[0],
        topicKind: parts[1]
    }
}

function getTopic(deviceId: string, topicKind: Exclude<TopicKind, 'unknown'>): string {
    return `/${deviceId}/${topicKind}`
}

function usesRequestProtocol(protocol?: string): boolean {
    return !!protocol && protocol !== LEGACY_PROTOCOL
}

function subscribeToInstrumentTopics(client: Client, instruments: Instrument[]) {
    instruments.forEach((instrument) => {
        const deviceId = instrument.deviceId

        client.subscribe(`/${deviceId}/${MQTT_API.TOPIC_ACTION.STATUS}`)
        client.subscribe(`/${deviceId}/${MQTT_API.TOPIC_ACTION.PRESENCE}`)
        client.subscribe(`/${deviceId}/${MQTT_API.TOPIC_ACTION.HEARTBEAT}`)
        client.subscribe(`/${deviceId}/${MQTT_API.TOPIC_ACTION.OTA_NOTIFICATION}`)
        client.subscribe(`/${deviceId}/${MQTT_API.TOPIC_ACTION.NOTIFICATION}`)
    })
}

export const MqttHandlerProvider = ({ children }: { children: ReactNode }) => {
    const currentUser = useCurrentUser()

    const customerInstruments = currentUser?.customer?.instruments ?? []

    const connectedInstruments = useConnectedInstruments()
    const updateConnectedInstruments = useUpdateConnectedInstrument()
    const updateOTAPayloadData = useChangeOTAUpdateData()

    const [mqttClient, setMqttClient] = useState<Client | undefined>(undefined)
    const [mqttIsConnected, setMqttIsConnected] = useState<boolean>(false)

    const shouldReconnectRef = useRef<boolean>(true)
    const isConnectingRef = useRef<boolean>(false)
    const reconnectTimeoutRef = useRef<number | undefined>(undefined)

    const lastHeartbeatRef = useRef<Record<string, number>>({})
    const offlineTimersRef = useRef<Record<string, number>>({})
    const offlineGenRef = useRef<Record<string, number>>({})

    const connectedInstrumentsRef = useRef(connectedInstruments)

    useEffect(() => {
        connectedInstrumentsRef.current = connectedInstruments
    }, [connectedInstruments])

    const instrumentByDeviceId = useMemo(() => {
        return new Map((customerInstruments ?? []).filter((instrument) => instrument?.deviceId).map((instrument) => [instrument.deviceId, instrument] as const))
    }, [customerInstruments])

    //////////////////////////////////////////////////////
    // INITIALIZE MQTT
    //////////////////////////////////////////////////////

    useEffect(() => {
        if (!currentUser?.customer?.id) return

        shouldReconnectRef.current = true

        const client = new Client(options.host, options.port, 'web' + generateRandomClientId())

        configureMqttClient(client)

        return () => {
            shouldReconnectRef.current = false

            client.onConnectionLost = () => {
                setMqttIsConnected(false)
            }

            if (reconnectTimeoutRef.current) {
                window.clearTimeout(reconnectTimeoutRef.current)
                reconnectTimeoutRef.current = undefined
            }

            isConnectingRef.current = false

            try {
                if (client.isConnected()) {
                    client.disconnect()
                }
            } catch {
                // ignore disconnect errors
            }

            cleanupOfflineTimers(offlineTimersRef, offlineGenRef)

            lastHeartbeatRef.current = {}

            setMqttIsConnected(false)
            setMqttClient(undefined)
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentUser?.customer?.id])

    //////////////////////////////////////////////////////
    // MQTT CLIENT READY
    //////////////////////////////////////////////////////

    useEffect(() => {
        if (!mqttClient?.isConnected()) {
            setMqttIsConnected(false)
            return
        }

        setMqttIsConnected(true)

        subscribeToInstrumentTopics(mqttClient, customerInstruments)
    }, [mqttClient, customerInstruments])

    //////////////////////////////////////////////////////
    // iOS PWA VISIBILITY RECONNECT
    //////////////////////////////////////////////////////

    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                console.log('App became visible')

                if (mqttClient && !mqttClient.isConnected()) {
                    console.log('Reconnecting MQTT after wakeup')

                    setTimeout(() => {
                        connectMqttClient(mqttClient)
                    }, 500)
                }
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            document.removeEventListener('visibilitychange', handleVisibilityChange)
        }
    }, [mqttClient])

    //////////////////////////////////////////////////////
    // iOS PWA ONLINE / OFFLINE
    //////////////////////////////////////////////////////

    useEffect(() => {
        const handleOnline = () => {
            console.log('Network restored')

            if (mqttClient && !mqttClient.isConnected()) {
                setTimeout(() => {
                    connectMqttClient(mqttClient)
                }, 500)
            }
        }

        const handleOffline = () => {
            console.log('Network lost')

            setMqttIsConnected(false)
        }

        window.addEventListener('online', handleOnline)
        window.addEventListener('offline', handleOffline)

        return () => {
            window.removeEventListener('online', handleOnline)
            window.removeEventListener('offline', handleOffline)
        }
    }, [mqttClient])

    //////////////////////////////////////////////////////
    // HEARTBEAT TIMEOUT CHECK
    //////////////////////////////////////////////////////

    useEffect(() => {
        if (!mqttClient?.isConnected()) return

        const intervalId = window.setInterval(() => {
            const now = Date.now()

            customerInstruments.forEach((instrument) => {
                const connectedInstrument = connectedInstrumentsRef.current.find((inst) => inst.deviceId === instrument.deviceId)

                const protocol = connectedInstrument?.protocol

                if (!usesRequestProtocol(protocol)) return

                const lastHeartbeat = lastHeartbeatRef.current[instrument.deviceId]

                if (!lastHeartbeat) return

                if (now - lastHeartbeat > OFFLINE_AFTER_MS) {
                    markOffline(connectedInstrument ?? instrument, updateConnectedInstruments)
                }
            })
        }, CHECK_EVERY_MS)

        return () => window.clearInterval(intervalId)
    }, [mqttClient, customerInstruments, updateConnectedInstruments])

    //////////////////////////////////////////////////////
    // HELPERS
    //////////////////////////////////////////////////////

    function getConnectedInstrument(deviceId: string): Instrument | undefined {
        return connectedInstrumentsRef.current.find((instrument) => instrument.deviceId === deviceId)
    }

    function getProtocol(deviceId: string): string | undefined {
        return getConnectedInstrument(deviceId)?.protocol
    }

    function send(topic: string, payload: string, qos = 0, retained = false) {
        console.log('Publishing to', topic, 'with payload', payload)

        const client = mqttClient

        if (!client || !client.isConnected()) {
            console.error('MQTT client not connected')
            return
        }

        client.send(topic, payload, qos as Qos, retained)
    }

    function publishJson(topic: string, payload: unknown, qos = 0, retained = false) {
        send(topic, JSON.stringify(payload), qos, retained)
    }

    function sendToMany(deviceIds: string[], sender: (deviceId: string) => void) {
        deviceIds.forEach(sender)
    }

    function getTargetIds(deviceId?: string): string[] {
        return deviceId ? [deviceId] : connectedInstruments.map((instrument) => instrument.deviceId)
    }

    //////////////////////////////////////////////////////
    // MESSAGE HANDLERS
    //////////////////////////////////////////////////////

    function handleV1Status(instrument: Instrument, payload: 'ONLINE' | 'OFFLINE') {
        const baseInstrument = getConnectedInstrument(instrument.deviceId) ?? instrument

        const instrumentWithProtocol: Instrument = {
            ...baseInstrument,
            protocol: LEGACY_PROTOCOL
        }

        if (payload === 'ONLINE') {
            markPresent(instrumentWithProtocol, updateConnectedInstruments)
        } else {
            markOffline(instrumentWithProtocol, updateConnectedInstruments)
        }
    }

    function handleV2Presence(instrument: Instrument, payload: 'ONLINE' | 'OFFLINE') {
        const baseInstrument = getConnectedInstrument(instrument.deviceId) ?? instrument

        const knownProtocol = baseInstrument.protocol

        const instrumentWithProtocol: Instrument = {
            ...baseInstrument,
            protocol: knownProtocol && knownProtocol !== LEGACY_PROTOCOL ? knownProtocol : DEFAULT_NEW_PROTOCOL
        }

        if (payload === 'ONLINE') {
            markPresent(instrumentWithProtocol, updateConnectedInstruments)
        } else {
            markOffline(instrumentWithProtocol, updateConnectedInstruments)
        }
    }

    function handleV2Heartbeat(instrument: Instrument, payload: string) {
        const heartbeatData = safeJsonParse<HeartbeatMessage>(payload)

        if (!heartbeatData) {
            console.warn('Invalid heartbeat JSON for device:', instrument.deviceId, payload)
            return
        }

        lastHeartbeatRef.current[instrument.deviceId] = Date.now()

        const baseInstrument = getConnectedInstrument(instrument.deviceId) ?? instrument

        const instrumentWithProtocol: Instrument = {
            ...baseInstrument,
            protocol: heartbeatData.protocol
        }

        updateFromHeartbeat(instrumentWithProtocol, updateConnectedInstruments, heartbeatData)
    }

    function handleNotification(instrument: Instrument, payload: string) {
        const notificationPayload = safeJsonParse<NotificationMessage>(payload)

        if (!notificationPayload) {
            console.warn('Invalid notification JSON for device:', instrument.deviceId, payload)
            return
        }

        const baseInstrument = getConnectedInstrument(instrument.deviceId) ?? instrument

        updateFromNotification(baseInstrument, updateConnectedInstruments, notificationPayload)
    }

    function handleOTANotification(instrument: Instrument, payload: string) {
        const otaPayload = safeJsonParse<any>(payload)

        if (!otaPayload) {
            console.warn('Invalid OTA notification payload:', payload)
            return
        }

        const existing = getConnectedInstrument(instrument.deviceId)

        if (otaPayload.type === 'version') {
            if (!existing) return

            updateConnectedInstruments({
                deviceId: existing.deviceId,
                serialNr: existing.serialNr,
                batteryLevel: existing.batteryLevel,
                volume: existing.volume,
                arrangement: existing.arrangement,
                type: existing.type,
                touchMode: existing.touchMode,
                firmware: existing.firmware,
                protocol: existing.protocol,
                currentSong: {
                    ...existing.currentSong
                }
            })

            return
        }

        if (otaPayload.type === 'progress') {
            updateOTAPayloadData(otaPayload)
            return
        }

        if (otaPayload.type === 'error') {
            updateOTAPayloadData(otaPayload)
            return
        }
    }

    //////////////////////////////////////////////////////
    // CONFIGURE MQTT CLIENT
    //////////////////////////////////////////////////////

    function configureMqttClient(client: Client) {
        client.onConnectionLost = (error) => {
            console.error(`MQTT connection lost: ${error.errorMessage}`)

            isConnectingRef.current = false
            setMqttIsConnected(false)

            scheduleReconnect(client)
        }

        client.onMessageArrived = (message: any) => {
            const topic = message.destinationName as string
            const payload = (message.payloadString ?? '').trim()

            const { deviceId, topicKind } = parseTopic(topic)

            if (!deviceId || !topicKind) return

            const instrument = instrumentByDeviceId.get(deviceId)

            if (!instrument) return

            if (topicKind === MQTT_API.TOPIC_ACTION.STATUS) {
                const presence = normalizePresence(payload)

                if (!presence) {
                    console.warn('Invalid V1 status payload:', payload)
                    return
                }

                handleV1Status(instrument, presence)
                return
            }

            if (topicKind === MQTT_API.TOPIC_ACTION.PRESENCE) {
                const presence = normalizePresence(payload)

                if (!presence) {
                    console.warn('Invalid V2 presence payload:', payload)
                    return
                }

                handleV2Presence(instrument, presence)
                return
            }

            if (topicKind === MQTT_API.TOPIC_ACTION.HEARTBEAT) {
                handleV2Heartbeat(instrument, payload)
                return
            }

            if (topicKind === MQTT_API.TOPIC_ACTION.OTA_NOTIFICATION) {
                handleOTANotification(instrument, payload)
                return
            }

            if (topicKind === MQTT_API.TOPIC_ACTION.NOTIFICATION) {
                handleNotification(instrument, payload)
                return
            }
        }

        connectMqttClient(client)
    }

    //////////////////////////////////////////////////////
    // CONNECT MQTT
    //////////////////////////////////////////////////////

    function scheduleReconnect(client: Client) {
        if (!shouldReconnectRef.current) return

        if (client.isConnected()) return

        if (reconnectTimeoutRef.current) {
            window.clearTimeout(reconnectTimeoutRef.current)
        }

        reconnectTimeoutRef.current = window.setTimeout(() => {
            reconnectTimeoutRef.current = undefined
            connectMqttClient(client)
        }, 1_000)
    }

    function connectMqttClient(client: Client) {
        //////////////////////////////////////////////////////
        // PREVENT DOUBLE CONNECTS
        //////////////////////////////////////////////////////

        if (client.isConnected()) {
            return
        }

        if (isConnectingRef.current) {
            return
        }

        isConnectingRef.current = true

        console.log('Connecting MQTT client...')

        try {
            client.connect({
                useSSL: true,

                //////////////////////////////////////////////////////
                // Use manual reconnect only. Do not combine this with
                // Paho's reconnect: true, because that can cause
                // AMQJS0011E Invalid state already connected.
                //////////////////////////////////////////////////////

                reconnect: false,

                onSuccess: () => {
                    console.log('Successfully connected MQTT client')

                    isConnectingRef.current = false
                    setMqttClient(client)
                    setMqttIsConnected(true)

                    subscribeToInstrumentTopics(client, customerInstruments)
                },

                onFailure: (error) => {
                    console.log('Connection failed:', error.errorMessage)

                    isConnectingRef.current = false
                    setMqttIsConnected(false)

                    scheduleReconnect(client)
                },

                userName: options.userName,
                password: options.password,
                keepAliveInterval: options.keepAliveInterval,
                cleanSession: options.cleanSession
            })
        } catch (error) {
            console.error('MQTT connect error:', error)

            isConnectingRef.current = false
            setMqttIsConnected(false)

            scheduleReconnect(client)
        }
    }

    //////////////////////////////////////////////////////
    // V1 COMMAND HELPERS
    //////////////////////////////////////////////////////

    const postChangeSong = ({ message, deviceId }: { message: string; deviceId: string; songType?: SONG_TYPES }) => {
        const payload = {
            message,
            timeStamp: Date.now()
        }

        publishJson(`/${deviceId}/${MQTT_API.TOPIC_ACTION.SONG}`, payload, 1, false)
    }

    const postChangeVolume = (message: string, deviceId?: string) => {
        if (!deviceId) return

        send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.VOLUME}`, message, 0, false)
    }

    const postChangeMasterVolume = (message: string) => {
        sendToMany(
            connectedInstruments.map((instrument) => instrument.deviceId),
            (deviceId) => send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.VOLUME}`, message, 0, false)
        )
    }

    const postResetNetwork = (deviceId: string) => {
        send(`/${deviceId}/${MQTT_API.TOPIC_ACTION.RESET_NETWORK}`, 'reset', 0, false)
    }

    const postSetInstrumentMode = ({ deviceId, touchMode }: { deviceId?: string; touchMode: TouchMode }) => {
        const payload = {
            touchMode: touchMode === 'EASY' ? 'simple' : 'hard'
        }

        sendToMany(getTargetIds(deviceId), (id) => {
            publishJson(`/${id}/${MQTT_API.TOPIC_ACTION.SET_PARAMETERS}`, payload, 0, false)
        })
    }

    //////////////////////////////////////////////////////
    // V2 REQUEST HELPERS
    //////////////////////////////////////////////////////

    const sendRequest = (deviceId: string, request: RequestName, params: unknown[] = []) => {
        publishJson(getTopic(deviceId, 'request'), { request, params }, 0, false)
    }

    //////////////////////////////////////////////////////
    // UNIFIED ACTIONS
    //////////////////////////////////////////////////////

    const changeArrangement = ({ arrangement, deviceId }: { arrangement: string; deviceId?: string }) => {
        sendToMany(getTargetIds(deviceId), (id) => {
            const protocol = getProtocol(id)

            if (usesRequestProtocol(protocol)) {
                sendRequest(id, 'arrangement', [arrangement])
            } else {
                postChangeSong({
                    message: arrangement,
                    deviceId: id
                })
            }
        })
    }

    const changeInstrumentMode = ({ deviceId, touchMode }: { deviceId?: string; touchMode: TouchMode }) => {
        const param = touchMode === 'EASY' ? 'simple' : 'hard'

        sendToMany(getTargetIds(deviceId), (id) => {
            const protocol = getProtocol(id)

            if (usesRequestProtocol(protocol)) {
                sendRequest(id, 'touchMode', [param])
            } else {
                postSetInstrumentMode({
                    deviceId: id,
                    touchMode
                })
            }
        })
    }

    const changeVolume = ({ volume, deviceId }: { volume: number; deviceId: string }) => {
        const protocol = getProtocol(deviceId)

        if (usesRequestProtocol(protocol)) {
            sendRequest(deviceId, 'volume', [volume])
        } else {
            postChangeVolume(String(volume), deviceId)
        }
    }

    const changeMasterVolume = ({ volume }: { volume: number }) => {
        sendToMany(
            connectedInstruments.map((instrument) => instrument.deviceId),
            (deviceId) => {
                const protocol = getProtocol(deviceId)

                if (usesRequestProtocol(protocol)) {
                    sendRequest(deviceId, 'volume', [volume])
                } else {
                    postChangeVolume(String(volume), deviceId)
                }
            }
        )
    }

    //////////////////////////////////////////////////////
    // OTA HELPERS
    //////////////////////////////////////////////////////

    const postSendOTAVersionRequest = (deviceId: string) => {
        publishJson(`/${deviceId}/${MQTT_API.TOPIC_ACTION.OTA_REQUEST}`, { request: 'version' }, 0, false)
    }

    const postSendOTAUpdateRequest = (deviceId: string, url: string) => {
        publishJson(
            `/${deviceId}/${MQTT_API.TOPIC_ACTION.OTA_REQUEST}`,
            {
                request: 'update',
                params: {
                    url
                }
            },
            0,
            false
        )
    }

    const postSendOTARebootRequest = (deviceId: string) => {
        publishJson(`/${deviceId}/${MQTT_API.TOPIC_ACTION.OTA_REQUEST}`, { request: 'reboot' }, 0, false)
    }

    return (
        <MqttHandlerContext.Provider
            value={{
                mqttIsConnected,
                postChangeSong,
                postChangeVolume,
                postChangeMasterVolume,
                postResetNetwork,
                postSetInstrumentMode,
                postSendOTAVersionRequest,
                postSendOTAUpdateRequest,
                postSendOTARebootRequest,
                changeArrangement,
                changeInstrumentMode,
                changeVolume,
                changeMasterVolume
            }}
        >
            {children}
        </MqttHandlerContext.Provider>
    )
}

export const useMqttIsConnected = () => useContext(MqttHandlerContext).mqttIsConnected
export const usePostMqttChangeSong = () => useContext(MqttHandlerContext).postChangeSong
export const usePostMqttChangeVolume = () => useContext(MqttHandlerContext).postChangeVolume
export const usePostMqttChangeMasterVolume = () => useContext(MqttHandlerContext).postChangeMasterVolume
export const usePostMqttResetNetwork = () => useContext(MqttHandlerContext).postResetNetwork
export const usePostSetInstrumentMode = () => useContext(MqttHandlerContext).postSetInstrumentMode
export const usePostSendOTAVersionRequest = () => useContext(MqttHandlerContext).postSendOTAVersionRequest
export const usePostSendOTAUpdateRequest = () => useContext(MqttHandlerContext).postSendOTAUpdateRequest
export const usePostSendOTARebootRequest = () => useContext(MqttHandlerContext).postSendOTARebootRequest
export const useChangeArrangement = () => useContext(MqttHandlerContext).changeArrangement
export const useChangeInstrumentMode = () => useContext(MqttHandlerContext).changeInstrumentMode
export const useChangeVolume = () => useContext(MqttHandlerContext).changeVolume
export const useChangeMasterVolume = () => useContext(MqttHandlerContext).changeMasterVolume
